import { Avatar, AvatarGroup, Stack, Typography, Box, useTheme } from '@mui/material';
import { memo, useMemo } from 'react';

type Props = {
  id: number;
  displayName: string;
  playerNumber: string;
  playerPosition: string;
  playerHeadshot: string;
  teamLogo: string;
  teamColor: string;
  isTrackedObject: boolean;
  readOnly?: boolean;
  sx?: object;
  disableNameEllipsis?: boolean;
};

export const TrackedItem = memo(({ disableNameEllipsis = false, ...playerItemData }: Props) => {
  const theme = useTheme();

  const color = useMemo(() => {
    return playerItemData.isTrackedObject
      ? theme.palette.cx.bg.surface
      : theme.palette.cx.light[200];
  }, [playerItemData.isTrackedObject]);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        alignItems: 'center',
        width: '98%',
        borderRadius: 1,
        backgroundColor:playerItemData.readOnly ? 'transparent' : playerItemData.isTrackedObject
          ? theme.palette.cx.light[200]
          : theme.palette.cx.bg.surface,
        px: 1,
        mb:'0.5px',
        height: 32,
        '&:hover': {
          color: playerItemData.teamColor,
        },
        ...playerItemData.sx,
      }}
    >
      <Box sx={{ position: 'relative', width: 36, height: 28 }}>
        <AvatarGroup
          spacing={16}
          sx={{
            '& .MuiAvatar-root': {
              border: 'none',
            },
            '& .MuiAvatar-root:first-of-type': {
              zIndex: 1,
              position: 'absolute',
              left: 14,
              top: 0,
              width: 28,
              height: 28,
              backgroundColor: `${playerItemData.teamColor}`,
            },
            '& .MuiAvatar-root:last-of-type': {
              zIndex: 2,
              position: 'absolute',
              right: 0,
              left: 14,
              bottom: 0,
              width: 28,
              height: 28,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
            },
          }}
        >
          <Avatar
            alt={playerItemData.displayName}
            src={playerItemData.playerHeadshot}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            }}
          />
          <Avatar
            alt="Team Logo"
            src={playerItemData.teamLogo}
            sx={{
              bgcolor: `${playerItemData.teamColor}`,
            }}
          >
            {playerItemData.displayName.charAt(0).toUpperCase()}
          </Avatar>
        </AvatarGroup>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          flex: 1,
          minWidth: 0,
          fontWeight: 400,
        }}
      >
        <Stack direction="row">
          <Box sx={{minWidth: 35}}>
            <Typography
              variant="body2"
              sx={{
                color,
                fontSize: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              #{playerItemData.playerNumber}
            </Typography>
          </Box>
          <Box sx={disableNameEllipsis ? undefined : { maxWidth: 85 }}>
            <Typography
              variant="body2"
              sx={{
                color,
                fontSize: '12px',
                ...(disableNameEllipsis
                  ? {}
                  : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
              }}
            >
              {playerItemData.displayName}
            </Typography>
          </Box>
        </Stack>
     
        <Box>
          {playerItemData.playerPosition && (
            <Typography
              variant="caption"
              sx={{
                color,
                fontSize: '0.75rem',
                fontWeight: 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                m: 0,
              }}
            >
              {playerItemData.playerPosition}
            </Typography>
          )}
        </Box>
      </Stack>
    </Stack>
  );
});
